import { ChallengeType, EntityType, GetCommentResponse, IntegrationException, LocationClueDetails } from '../types';
import { TemplateResult, css, html, unsafeCSS } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import challengeData, { challengeLocationsDisplayName } from '../helpers/state/challenge-data';
import { customElement, property, state } from 'lit/decorators';
import { getCheckinKey, getCheckinTimesString } from '../helpers/api/checkin-api';
import { getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { getVisibleTextColor, makeColorsIntoNumberSvg } from '../helpers/colors';
import { isDefined, isFalsy, isValidString, shouldDisplayAddPointElement } from '../helpers/utils';

import { renderEmbedlyCards } from '../helpers/embeds';
import { PointCardBase } from './point-card-base';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { addPointView } from '../helpers/api/topics-api';
import { getImageUrls } from '../helpers/components/point-card';
import { heights } from '../style/variables';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import { shouldRevealSpecialOffer } from '../helpers/challenge-special-offers';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('challenge-point-card')
export class ChallengePointCard extends PointCardBase {
    static amplitude_callsite: string = 'Challenge Point Card';

    @property({ type: Number })
    challenge_points?: number;

    @state()
    isCheckedin = false;

    @state()
    showSpecialOffer: boolean = true;

    @state()
    eventString: string = '';

    @state()
    customFormInputs: Array<{ [key: string]: string | TemplateResult }> = [];

    @state()
    clue_details?: LocationClueDetails;

    @state()
    allComments: GetCommentResponse[] = [];

    @state()
    showComments: boolean = false;

    @property({ type: Boolean })
    is_saved_point: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindChallengeData = new StateController(this, challengeData);

    async connectedCallback() {
        super.connectedCallback();
        this.updateCheckinStatus();
    }

    private async commentChanged() {
        this.allComments = await challengeData.getInternalNotesForChallengePoint(this.point?._id.$oid ?? '');
    }

    private onToggleComments() {
        this.showComments = !this.showComments;
    }

    async firstUpdated() {
        // Track challenge point view/display only once when component is first rendered
        // (prevents duplicate tracking if parent component updates the point property multiple times)
        if (this.point) {
            try {
                await addPointView(ChallengePointCard.amplitude_callsite, topicData.topicId, this.point._id.$oid, 'challenge_card');
            } catch (e) {
                console.error('Error tracking point view', e);
            }
        }
    }

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('point')) {

            if (topicData.topic?.has_dates === true) {
                this.eventString = await this.getEventString() ?? '';
            };
            if (this.point && this.imageUrls.length === 0) {
                this.imageUrls = await getImageUrls(this.point, topicData.topic, topicData.topicKey, PointCardBase.amplitude_callsite);
            }
            this.customFormInputs = [];
            this.customFormInputs = this.customFieldCardDisplay;

            // Load internal notes if challenge has internal notes enabled
            if (challengeData.challenge?.allow_internal_notes === true) {
                try {
                    this.allComments = await challengeData.getInternalNotesForChallengePoint(this.point?._id.$oid ?? '');
                } catch (e) {
                    console.error('Error getting comments', e);
                }
                // Roswell uses the notes area as a rating form — open it by
                // default for each point so participants don't have to tap in.
                if (topicData.hasExceptions === IntegrationException.Roswell) {
                    this.showComments = true;
                }
            }
        }

        if (changedProperties.has('point') && isDefined(this.point)) {
            renderEmbedlyCards(this.shadowRoot?.querySelectorAll('a[href*="proxi_embed=true"]') ?? []);
        }
    }

    private updateCheckinStatus() {
        if (isDefined(this.point)) {
            this.isCheckedin = pointCache.isUserCheckedIn(this.point);
            this.showSpecialOffer = true;

            if (topicData.hasChallenge) {
                const challenge = challengeData.challenge;
                if (!challenge) {
                    return;
                }
                this.showSpecialOffer = shouldRevealSpecialOffer({
                    challenge,
                    isCheckedIn: this.isCheckedin,
                    isGuide: false,
                });
            }
        }
    }

    private parseDomain(url: string) {
        let domain = url;

        // Remove protocol (e.g., "http://", "https://")
        if (domain.includes('://')) {
            domain = domain.split('://')[1];
        }

        // Remove path (e.g., "/path/to/page")
        if (domain.includes('/')) {
            domain = domain.split('/')[0];
        }

        // Remove www subdomain
        if (domain.indexOf('www.') === 0) {
            domain = domain.substring(4);
        }

        return domain;
    }

    private distance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        if (lat1 === lat2 && lon1 === lon2) {
            return 0;
        } else {
            const radlat1 = (Math.PI * lat1) / 180;
            const radlat2 = (Math.PI * lat2) / 180;
            const theta = lon1 - lon2;
            const radtheta = (Math.PI * theta) / 180;
            let dist =
                Math.sin(radlat1) * Math.sin(radlat2) +
                Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if (dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = (dist * 180) / Math.PI;
            dist = dist * 60 * 1.1515;
            dist = dist * 1.609344 * 1000; // convert to meters
            return dist;
        }
    }

    private async viewInMapClick() {
        if (this.associatedMarker && this.point) {
            await topicData.mapComponent?.focusMarker(this.associatedMarker,this.point);
        }

        logWithAmplitude(ChallengePointCard.amplitude_callsite, 'View In Map Click', {
            point_id: this.point_id,
            topic_id: topicData.topicId,
        });
    }

    closePointCard() {
        topicData.mapComponent?.closeListOfPoints();
    }

    get checkInTimes() {
        if (this.isCheckedin && this.point && isDefined(this.point.location)) {
            const checkins = challengeData.allCheckins.get(getCheckinKey(this.point.location));
            return getCheckinTimesString(checkins);
        }
        return undefined;
    }

    render() {
        return html`
            <div class="expanded-point-card">
                <div class="expanded-point-card__image-wrapper">
                    ${this.hasImages
                        ? html`<image-carousel
                                    @image-error="${(e: CustomEvent) => this.handleImageError(e.detail)}"
                                    borderRadius="4" .height=${'170px'} .imageUrls=${this.displayableImageUrls} .mobilePreview=${false}>
                                </image-carousel>`
                        : ''}
                    <div class="expanded-point-card__content">
                        <span class="mini-point-card__category-wrapper">
                            ${topicData.topic?.enable_point_numbering === true && this.point?.number !== undefined
                                ? html`<img src="${makeColorsIntoNumberSvg(getNumberedPointIconColor(topicData.topic, this.point.topic_category_id.$oid), this.point?.number, getNumberedPointIconShape(topicData.topic, this.point.topic_category_id.$oid))}"
                                            class="mini-point-card__point-number"
                                            width="28"
                                            height="28"/>`
                                : ''
                            }
                            <span class="mini-point-card__category-pill">
                                <img src="${this.category?.icon ?? ''}" width="12" height="12"/>
                                ${keyed(this.category?.name, html`<translated-span>${this.category?.name}</translated-span>`)}
                            </span>
                            ${this.challenge_points !== undefined
                                ? html`<span class="mini-point-card__category-pill">
                                        ${this.challenge_points} ${this.challenge_points === 1 ? html`<translated-span>point</translated-span>` : html`<translated-span>points</translated-span>`}
                                    </span>`
                                : ''
                            }
                        </span>
                        ${shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.title?.hide_on_point_card, false)
                            ? html`
                                  <span class="expanded-point-card__title">
                                      ${this.point?.featured === true
                                          ? html`<mwc-icon class="expanded-expanded-point-card__title--featured" aria-hidden="true">star</mwc-icon>`
                                          : ''}
                                      ${isValidString(this.point?.name)
                                          ? html`<h2 class="expanded-expanded-point-card__title-text">${this.point?.featured === true ? `${topicData.topic?.featured_name ?? 'Featured'}:` : ''} ${this.point?.name}</h2>`
                                          : ''}
                                  </span>`
                            : ''}
                        ${this.point?.location.place_id !== 'custom' &&
                        isFalsy(topicData.topic?.disable_location_ui) &&
                        shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.location?.hide_on_point_card, false)
                            ? html`
                                  <div class="expanded-point-card__address">
                                      ${this.point?.location.search === 'Shape' ? '' : this.point?.location.search}
                                  </div>`
                            : ''}
                        ${isValidString(this.eventString) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.date_time?.hide_on_point_card, false)
                            ? html`
                                  <div class="expanded-point-card__event">
                                      <mwc-icon class="expanded-point-card__event-icon" aria-hidden="true">calendar_month</mwc-icon>
                                      ${this.eventString}
                                  </div>`
                            : ''}
                        ${this.showSpecialOffer &&
                        isValidString(this.point?.special_offer?.description) &&
                        shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.special_offer?.hide_on_point_card, false)
                            ? html`
                                  <span class="expanded-point-card__special-offer">
                                      <b>${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'}:</b>
                                      ${this.point?.special_offer?.description}
                                      ${this.checkInTimes && this.isCheckedin ? html`<span class="small-text"> (Activated by ${challengeData.activated_challenge?.display_name ?? 'user'} - ${this.checkInTimes})</span>` : ''}
                                  </span>`
                            : ''}
                        ${!this.showSpecialOffer && isValidString(this.point?.special_offer?.description) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.special_offer?.hide_on_point_card, false)
                            ? html`
                                  <span class="expanded-point-card__special-offer">
                                      ${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Check in to see the special offer!'}
                                    </span>`
                            : ''}
                        ${isValidString(this.descriptionString) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.description?.hide_on_point_card)
                                ? html` <span class="expanded-point-card__description">
                                    ${html`${unsafeHTML(this.descriptionString)}`}
                                </span>`
                                : ''}
                        ${this.customFormInputs.map((input) => {
                            return html`
                                ${Object.keys(input).map((key) => {
                                    return html`
                                        <span class="expanded-point-card__custom-form">
                                            <br>
                                            <b>${key}:</b> ${input[key]}
                                        </span>
                                    `;
                                })}
                            `;
                        })}
                        ${isDefined(this.point) &&
                        isDefined(this.point.source) &&
                        isValidString(this.point.source.source_url) &&
                        topicData.hasExceptions !== IntegrationException.JLLRealEstate
                            ? html`
                                  <div class="expanded-point-card__source-wrapper">
                                      Pinned from:
                                      <a
                                          class="expanded-point-card__source"
                                          target="${topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank'}"
                                          href=${this.point.source.source_url}
                                      >
                                          ${this.point?.source.source_name ?? this.parseDomain(this.point.source.source_url)}
                                      </a>
                                  </div>`
                            : ''}
                        ${this.pointTags.length > 0 &&
                        shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.tags?.hide_on_point_card, false)
                            ? html`
                                  <div class="point-card__tags-wrapper">
                                      ${this.pointTags?.map(
                                          (item) => html`
                                              <div
                                                  class="expanded-point-card__tag"
                                                  style="background-color: ${item.icon}; color: ${getVisibleTextColor(item.icon)}"
                                              >
                                                  <mwc-icon class="expanded-point-card__tag-icon" aria-hidden="true">sell</mwc-icon>
                                                  <translated-span>${item.name}</translated-span>
                                              </div>
                                          `
                                      )}
                                  </div>`
                            : ''}
                        ${this.clue_details?.clue && this.clue_details?.clue !== '' && !challengeData.isType(ChallengeType.Treasure) ? html`
                            <div class="expanded-point-card__address">
                                ${challengeLocationsDisplayName(challengeData.challenge, false, 'title')}: ${this.clue_details?.clue}
                            </div>
                        ` : ''}
                        ${this.clue_details?.hint && this.clue_details?.hint !== '' && !challengeData.isType(ChallengeType.Treasure) ? html`
                            <div class="expanded-point-card__address">
                                Hint: ${this.clue_details?.hint}
                            </div>
                        ` : ''}
                    </div>
                    ${this.checkInTimes && this.isCheckedin ? html`
                        <span class="small-text"> <translated-span>Visited:</translated-span> ${this.checkInTimes}</span>
                    ` : ''}
                    ${challengeData.challenge?.checkin_frequency_recurrance && challengeData.challenge?.checkin_frequency_recurrance !== 'once' && challengeData.challenge?.challenge_type !== 'guide' ? html`
                        <span class="small-text">
                            <translated-span>You may check in up to</translated-span> ${challengeData.challenge.checkin_frequency_quantity} ${challengeData.challenge.checkin_frequency_quantity > 1 ? html`<translated-span>times</translated-span>` : html`<translated-span>time</translated-span>`}  <translated-span>${challengeData.challenge?.checkin_frequency_recurrance}</translated-span> <translated-span>for points</translated-span>
                        </span>
                    ` : ''}

                    ${challengeData.challenge?.allow_internal_notes === true ? html`
                        <div class="internal-notes-toggle" @click="${this.onToggleComments}">
                            <mwc-icon class="expanded-point-card__event-icon" aria-hidden="true">note</mwc-icon>
                            <span>${topicData.hasExceptions === IntegrationException.Roswell
                                ? `${this.showComments ? 'Hide' : 'Show'} rating form`
                                : `${this.showComments ? 'Hide' : 'View'} Internal Notes ${this.allComments.length > 0 ? `(${this.allComments.length})` : ''}`}</span>
                        </div>
                    ` : ''}

                    ${this.showComments ? html`
                        <div class="expanded-point-card__form">
                            <comment-component
                                @close=${this.onToggleComments}
                                @comment-changed=${async () => await this.commentChanged()}
                                .allComments=${this.allComments}
                                .entity_id=${this.point?._id.$oid}
                                .entity_type=${EntityType.Point}>
                            </comment-component>
                        </div>
                    ` : ''}

                    ${challengeData.isType(ChallengeType.Guide) ? html`
                        <div style="padding: 8px 0; display: flex;">
                            ${!this.is_saved_point ? html`
                                <form-button
                                    raised
                                    icon="save"
                                    size="sm"
                                    .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                                    @click=${(e: Event) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('on-save-point'));}}
                                    >
                                    Save
                                </form-button>
                            ` : html`
                                <form-button
                                    raised
                                    icon="check"
                                    size="sm"
                                    .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                                    @click=${(e: Event) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('on-save-point'));}}
                                    >
                                    Saved
                                </form-button>
                            `}
                        </div>
                    ` : ''}
                    <point-card-actions
                        .point=${this.point}
                        .hideCreateMap=${true}
                        .mobilePreview=${false}
                    ></point-card-actions>
                </div>
            </div>

            <mwc-dialog class="dialog" hideActions>
                <modal-dialog size="lg">Add To Map</modal-dialog>
            </mwc-dialog>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 32px;
            }

            /* width */
            .expanded-point-card__content::-webkit-scrollbar, .expanded-point-card__content--horizontal::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .expanded-point-card__content::-webkit-scrollbar-track, .expanded-point-card__content--horizontal::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .expanded-point-card__content::-webkit-scrollbar-thumb, .expanded-point-card__content--horizontal::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .expanded-point-card__content {
                padding: 8px 16px;
                flex: 1 1 auto;
                overflow: hidden auto;
                font-size: .75rem;
            }

            .expanded-point-card {
                width: 100%;
                height: 100%;
                display: flex;
                background: white;
                z-index: 1;
                flex-direction: column;
            }

            .admin-info-icon {
                --mdc-icon-size: 16px;
                color: var(--main-featured-color);
            }

            .expanded-point-card--discovery {
                background: transparent;
                border: none;
                border-bottom: 0.5px solid var(--theme-input-border-color);
                word-wrap: break-word;
                margin-left: 8px;
            }

            .no-bottom-border {
                border-bottom: none;
            }

            .expanded-point-card__discovery-content {
                float: left;
                width: 100%;
            }

            .expanded-point-card__content--discovery {
                display: flex;
                margin: 4x 8px;
                justify-content: space-between;
                flex-direction: column;
            }

            .expanded-point-card__content--list-of-points {
                display: flex;
                margin: 0;
                justify-content: space-between;
                flex-direction: column;
            }

            .emailInputForm {
                flex: 1;
            }

            .expanded-point-card--horizontal {
                width: 100%;
                display: flex;
                flex-direction: column;
                max-height: calc(90dvh - ${unsafeCSS(heights.nav)} - 50px);
            }

            .expanded-point-card__form {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                margin-top: 10px;
                gap: 10px;
                background-color: var(--theme-light-scrollbar-color);
                padding: 1rem;
            }

            .expanded-point-card--backpack {
                width: 100%;
                display: flex;
                flex-direction: column;
                height: 300px;
            }

            .expanded-point-card__content--horizontal .expanded-expanded-point-card__title-text {
                font-size: 18px;
                align-self: center;
            }

            .expanded-point-card__point-number {
                margin-right: -5px;
                z-index: 2;
            }

            .expanded-point-card__custom-icon {
                margin-right: -5px;
                z-index: 1;
                border-radius: 100%;
            }

            .expanded-point-card__checked-in {
                margin-right: -5px;
                z-index: 3;
            }

            .expanded-point-card__special-offer {
                border: 1px dashed var(--theme-input-color);
                padding: 4px;
                margin-top: 8px;
                display: inline-block;
            }

            .expanded-point-card__menu-wrapper {
                display: flex;
                align-items: center;
                position: absolute;
                top: 4px;
                right: 0;
                z-index: 2;
            }

            .expanded-point-card__edit,
            .expanded-point-card__info,
            .expanded-point-card__close_admin {
                background-color: var(--main-featured-color);
                margin-right: 4px;
                border-radius: 4px;
                border:none;
                --mdc-theme-on-primary: var(--theme-input-color);
            }

            .expanded-point-card__upvote,
            .expanded-point-card__share,
            .expanded-point-card__close_cc {
                background-color: white;
                box-shadow: rgb(31 27 45 / 12%) 0px 2px 8px -4px, rgb(31 27 45 / 12%) 0px 4px 16px;
                --mdc-icon-size: 18px;
                border-radius: 40px;
                margin-right: 6px;
                --mdc-icon-button-size: 30px;
            }

            .expanded-point-card__upvote--user-upvoted {
                background-color: var(--theme-input-color);
                color: white;
            }

            .expanded-point-card__category-wrapper {
                display: flex;
                align-items: center;
                position: absolute;
                bottom: 0;
                background: white;
                padding: 0 10px;
            }

            .expanded-point-card__booking {
                display: flex;
                flex-direction: row;
                flex-wrap: none;
                overflow-x: scroll;
                justify-content: flex-start;
                gap: 5px;
                color: var(--theme-input-color);
            }

            .expanded-point-card__booking::-webkit-scrollbar {
                display: none;
            }

            .expanded-point-card__associated-map {
                color: var(--theme-input-color);
                border-radius: 9px;
                background: var(--theme-light-scrollbar-color);
                padding: 2px 6px;
                border: 1px solid var(--theme-input-border-color);
                font-weight: bold;
                font-size: 12px;
                letter-spacing: 0.5px;
                text-decoration: none;
                cursor: pointer;
                white-space: nowrap;
                overflow: hidden;
                max-width: 300px;
                display: inline-block;
                text-overflow: ellipsis;
            }

            .expanded-point-card__associated-wrapper {
                padding-top: 10px;
            }

            .expanded-point-card__image-wrapper {
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .expanded-point-card__image-wrapper--no-image {
                position: static;
                display: flex;
                justify-content: space-between;
                padding: 4px 0px;
            }

            .expanded-point-card__image-wrapper--no-image .expanded-point-card__category-wrapper {
                position: static;
            }

            .expanded-point-card__image-wrapper--no-image .expanded-point-card__menu-wrapper {
                position: static;
            }

            .expanded-expanded-point-card__title-text {
                font-weight: 700;
                font-size: 1rem;
                margin-bottom: 5px;
                color: var(--main-primary-color);
            }

            .expanded-point-card__source {
                color: var(--theme-input-color);
                border-radius: 9px;
                background: var(--theme-light-scrollbar-color);
                padding: 2px 6px;
                border: 1px solid var(--theme-input-border-color);
                font-weight: bold;
                font-size: 12px;
                letter-spacing: 0.5px;
                text-decoration: none;
            }

            .expanded-point-card__source-wrapper {
                padding-top: 10px;
            }

            .expanded-point-card__address {
                color: var(--theme-input-color);
                margin-top: 5px;
            }

            .expanded-point-card__address-icon,
            .expanded-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 14px;
                width: 14px;
            }

            .expanded-point-card__event {
                color: var(--theme-input-color);
            }

            .expanded-point-card__description {
                color: var(--theme-input-color);
            }

            .expanded-expanded-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .expanded-point-card__title {
                display: flex;
            }

            .point-card__tags-wrapper {
                display: flex;
                flex-wrap: wrap;
                padding: 8px 0;
            }

            .expanded-point-card__tag {
                user-select: none;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 4px 8px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid lightgray;
                background: #fff;
                filter: drop-shadow(rgba(0, 0, 0, 0.25) 0px 1px 1px);
            }

            .expanded-point-card__tag-icon {
                --mdc-icon-size: 16px;
                padding-right: 4px;
            }

            .small-text {
                font-size: 0.75rem;
                line-height: 0.75rem;
            }

            .admin-action-list-holder {
                margin-bottom: 5px;
                margin-top: 0;
            }

            .admin-actions-list {
                padding: 5px;
                background-color: var(--main-featured-color);
                margin: 5px;
                border-radius: 5px;
                cursor: pointer;
                white-space: nowrap;

            }
            .hidden {
                display: none;
            }

            .mini-point-card__category-wrapper {
                display: flex;
                gap: 5px;
            }

            .mini-point-card__category-pill {
                border-radius: 20px;
                font-size: 12px;
                padding: 2px 6px;
                gap: 3px;
                display: flex;
                align-items: center;
                border: 1px solid var(--theme-border-color);
            }

            .mini-point-card__image-wrapper {
                display: flex;
                width: 25%;
                background-color: var(--theme-white-color);
                overflow: hidden;
                align-items: center;
                opacity: 0.98;
            }

            .wrapper-icon {
                background-color: var(--theme-border-color);
                justify-content: center;
            }

            .space-wrapper {
                flex: 1;
                background-color: var(--theme-white-color);
            }

            .internal-notes-toggle {
                display: flex;
                align-items: center;
                gap: 5px;
                cursor: pointer;
                color: var(--main-primary-color);
                padding: 10px 0;
                font-size: 14px;
            }

            .internal-notes-toggle:hover {
                text-decoration: underline;
            }


            @media only screen and (max-width: 550px) {
                .expanded-point-card--horizontal_admin {
                    max-height: calc(
                        70dvh - ${unsafeCSS(heights.nav)} -
                            ${unsafeCSS(heights.mobileSettingsControls)} - 125px
                    );
                    display: flex;
                    flex-direction: column;
                }

                .expanded-point-card--horizontal_cc {
                    max-height: calc(90dvh - ${unsafeCSS(heights.nav)} - 75px);
                    display: flex;
                    flex-direction: column;
                }

                .expanded-point-card--discovery {
                    max-height: none;
                    display: block;
                }

                .expanded-point-card__content--horizontal {
                    margin-top: 5px;
                    margin-bottom: 5px;
                    overflow: hidden auto;
                    flex: 1 1 auto;
                }
            }
        `;
    }
}
